import * as React from 'react';
import styled from 'styled-components';
import { ISegmentFilter } from 'sections/offsite-campaigns/entities/offsiteCampaign';
import { State as CampaignState } from 'entities/lifecycle/lifeCycle';
import { SegmentFilterListItem } from './segment-filter-items/SegmentFilterListItem';
import { useTranslation } from 'react-i18next';
import { Button, Icon, theme } from '@salecycle/ui-component-library';
import Block from '../../common/Block';
import { segmentationOptionList } from './segment-filter-items/constants';
import {
  OPERATORS_TRANSLATION_PREFIX_DEFAULT,
  VALUES_TRANSLATION_PREFIX_DEFAULT,
} from '@salecycle/experimental/types/types';
import { renderStringFilterValue } from './segment-filter-items/utils/helpers';

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;

interface OwnProps {
  segmentId: string;
  segmentFilters: ISegmentFilter[];
  campaignState: CampaignState;
  onCustomFilterClick: () => void;
}

export type Props = OwnProps;

export const SegmentFilterList: React.FC<Props> = ({
  segmentId,
  segmentFilters,
  campaignState,
  onCustomFilterClick,
}) => {
  const defaultSegment = {
    type: 'all',
  } as ISegmentFilter;

  const { t } = useTranslation('campaigns');

  const filterSummaryList: string[] = React.useMemo(
    () =>
      segmentFilters
        ?.filter(f => f.type === 'custom')
        .map(filter => {
          const filterOptionPath = filter.options.join('.*.');

          const filterOption = segmentationOptionList[filterOptionPath];

          let output = '';
          // filter type
          output += t(`segmentationRules.types.${filterOptionPath.replaceAll('.', '_')}`);
          // condition / operator
          const opsTPrefix = filterOption?.operatorsTranslationPrefix ?? OPERATORS_TRANSLATION_PREFIX_DEFAULT;

          if (filterOption?.filterType === 'bool') {
            output += ' ';
          } else {
            output += ' ' + t(`${opsTPrefix}.${filter.comparison}`) + ' ';
          }
          // value
          switch (filterOption?.filterType) {
            case 'string': {
              renderStringFilterValue(filter.comparison) && (output += filter.value);
              break;
            }
            case 'bool': {
              output += t(`${VALUES_TRANSLATION_PREFIX_DEFAULT}.${filter.value}`);
              break;
            }
            case 'array': {
              const tPrefix = filterOption.valuesTranslationPrefix ?? VALUES_TRANSLATION_PREFIX_DEFAULT;
              output += t(`${tPrefix}.${filter.value}`);
              break;
            }
            case 'number': {
              output += filter.value ?? '';
              break;
            }
          }
          // TODO: update to mirror onsite ul display?
          return output;
        }) ?? [],
    [segmentFilters, t],
  );

  const filterSummary = (
    <>
      {t('offsiteEditor.segments.filters.summary.opening')}
      <br />
      {filterSummaryList.map((str, idx) => {
        return idx !== filterSummaryList.length - 1 ? (
          <span key={`filter-summary-item-${idx}`}>
            {str} AND
            <br />
          </span>
        ) : (
          <span key={`filter-summary-item-${idx}`}>
            {str}
            <br />
          </span>
        );
      })}
    </>
  );

  return (
    <>
      {segmentFilters && segmentFilters.length > 0 && (
        <div>
          {/* Renders the summary fo the custom filters */}
          {segmentFilters.some(f => f.type === 'custom') && (
            <Block
              icon="envelope"
              iconColor={theme.vars.newColor.blue2}
              title={t('offsiteEditor.segments.filters.summary.title')}
              value={filterSummary}
              campaignState={campaignState}
              valid={true}
              form={<></>}
              disableOpen={true}
              controls={
                <StyledButtonContainer>
                  <Button
                    title={t('campaigns:offsiteEditor.segments.filters.summary.button')}
                    variation="secondary"
                    leftSideIcon={<Icon icon="cog" />}
                    onClick={onCustomFilterClick}
                  ></Button>
                </StyledButtonContainer>
              }
            />
          )}
        </div>
      )}

      {/* Renders a box to show no filters specified */}
      {(!segmentFilters || segmentFilters.length < 1) && (
        <SegmentFilterListItem
          campaignState={campaignState}
          index={0}
          segmentId={segmentId}
          segmentFilter={defaultSegment}
          onCustomFilterClick={onCustomFilterClick}
        />
      )}
    </>
  );
};
